/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, CheckCircle2, XCircle } from "lucide-react";
import { assets } from "../assets/assets";
import { post, completeSignIn } from "../utils/service";

interface SetPasswordResponse {
  success: boolean;
  message: string;
}

interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    user: {
      id: string;
      first_name: string;
      last_name: string;
      email: string;
      phone: string;
      role: string;
      preferred_currency: string;
      is_active: boolean;
      created_at: string;
      updated_at: string;
    };
    token: string;
    refresh_token: string;
  };
  timestamp: string;
}

const GlassInputWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className="rounded-2xl border border-gray-200 bg-white transition-colors focus-within:border-[#ff6d27] focus-within:bg-white">
    {children}
  </div>
);

const SetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const email = searchParams.get("email");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token || !email) {
      toast.error("This password link is incomplete. Request a new one.");
      navigate("/forgot-password");
    }
  }, [token, email, navigate]);

  const requirements = [
    { label: "At least 10 characters", met: password.length >= 10 },
    { label: "Upper and lower case letters", met: /[A-Z]/.test(password) && /[a-z]/.test(password) },
    { label: "One number", met: /[0-9]/.test(password) },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");

    if (!token || !email) return;

    if (requirements.some((r) => !r.met)) {
      setError("Please meet all the password requirements.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const response = await post<SetPasswordResponse>("/auth/reset-password", {
        email,
        token,
        password,
      });

      if (response.success) {
        toast.success("Your password is set");

        const loginResponse = await post<LoginResponse>("/auth/login", {
          email,
          password,
        });

        if (loginResponse.success) {
          const { hasOrganization, platformAdmin } = await completeSignIn(loginResponse.data, true);
          navigate(platformAdmin ? "/platform" : hasOrganization ? "/" : "/login");
        } else {
          navigate("/login");
        }
      }
    } catch (err: any) {
      const errors = err?.response?.data?.errors as Record<string, string[]> | undefined;
      const message = (errors && Object.values(errors).flat()[0]) || err?.response?.data?.message || "This link may have expired. Request a new one from the sign-in page.";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen flex flex-col md:flex-row font-geist w-screen bg-white">
      <section className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="flex flex-col gap-6">
            <div className="flex justify-start">
              <img src={assets.MainLogo} alt="Logo" className="h-16 w-auto object-contain" />
            </div>

            <h1 className="text-4xl md:text-5xl font-semibold leading-tight text-gray-900">
              <span className="font-light tracking-tighter">Set your password</span>
            </h1>
            <p className="text-gray-600 text-xs">
              Choose a password for {email}. You'll be signed in right after.
            </p>

            <form className="space-y-5" onSubmit={handleSubmit}>
              <div>
                <label className="text-xs font-medium text-gray-700">Password</label>
                <GlassInputWrapper>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter a new password"
                      className="w-full bg-transparent text-xs p-4 pr-12 rounded-2xl focus:outline-none text-gray-900"
                      disabled={loading}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-3 flex items-center"
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4 text-gray-400 hover:text-gray-900 transition-colors" />
                      ) : (
                        <Eye className="w-4 h-4 text-gray-400 hover:text-gray-900 transition-colors" />
                      )}
                    </button>
                  </div>
                </GlassInputWrapper>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-700">Confirm Password</label>
                <GlassInputWrapper>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password"
                    className="w-full bg-transparent text-xs p-4 rounded-2xl focus:outline-none text-gray-900"
                    disabled={loading}
                    required
                  />
                </GlassInputWrapper>
              </div>

              <div className="space-y-1.5">
                {requirements.map((req) => (
                  <div key={req.label} className="flex items-center gap-2 text-xs">
                    {req.met ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                    ) : (
                      <XCircle className="w-3.5 h-3.5 text-gray-300" />
                    )}
                    <span className={req.met ? "text-gray-700" : "text-gray-400"}>{req.label}</span>
                  </div>
                ))}
              </div>

              {error && (
                <p className="text-red-500 text-xs">
                  {error}{" "}
                  <Link to="/forgot-password" className="font-medium text-[#ff6d27] hover:underline">Request a new link</Link>
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-2xl bg-[#ff6d27] py-4 font-medium text-white hover:bg-[#E85A1B] transition-colors text-xs disabled:opacity-50"
              >
                {loading ? "Setting password..." : "Set password & sign in"}
              </button>

              <p className="text-center text-xs text-gray-500">
                <Link to="/login" className="text-[#ff6d27] hover:underline">
                  Back to login
                </Link>
              </p>
            </form>
          </div>
        </div>
      </section>

      <section className="hidden md:block flex-1 relative p-4">
        <div
          className="absolute inset-4 rounded-3xl bg-cover bg-center shadow-2xl"
          style={{ backgroundImage: `url(${assets.LoginBanner})` }}
        ></div>
      </section>
    </div>
  );
};

export default SetPassword;
